/**
 * WeeklySummary component - Displays weekly adherence stats and body state distribution.
 * Shown every 7th consecutive check-in day to reinforce identity shift.
 */

import React from 'react';
import { WeeklySummary as WeeklySummaryType } from '../../types';

interface WeeklySummaryProps {
  summary: WeeklySummaryType;
}

const STATE_LABELS: Record<string, { label: string; color: string }> = {
  balanced: { label: 'Balanced', color: 'bg-green-100 text-green-800' },
  high_energy: { label: 'High Energy', color: 'bg-blue-100 text-blue-800' },
  recovery: { label: 'Recovery', color: 'bg-orange-100 text-orange-800' },
  hormonal_dip: { label: 'Hormonal Dip', color: 'bg-purple-100 text-purple-800' },
  hormonal_shift: { label: 'Hormonal Shift', color: 'bg-pink-100 text-pink-800' },
};

const WeeklySummaryDisplay: React.FC<WeeklySummaryProps> = ({ summary }) => {
  const adherencePercent = Math.round(summary.adherence_rate);
  const partialPercent = Math.round(summary.partial_rate);

  return (
    <div className="bg-gradient-to-br from-[#FFF5E9] to-white rounded-2xl border border-[#E5D5C3] p-6 space-y-4">
      <div className="flex items-center space-x-2">
        <span className="text-2xl">🏆</span>
        <h3 className="text-lg font-bold text-[#6B5D4F] font-[Nunito]">
          Week {summary.week_number} Summary
        </h3>
      </div>

      {/* Adherence Rate */}
      <div className="space-y-2">
        <div className="flex items-baseline justify-between">
          <span className="text-sm font-medium text-neutral-600">Guidance Followed</span>
          <span className="text-2xl font-bold text-[#C98CCC]">{adherencePercent}%</span>
        </div>
        <div className="w-full bg-neutral-200 rounded-full h-2.5">
          <div
            className="bg-[#C98CCC] h-2.5 rounded-full transition-all"
            style={{ width: `${adherencePercent}%` }}
          />
        </div>
        {partialPercent > 0 && (
          <p className="text-xs text-neutral-500">
            + {partialPercent}% partially followed
          </p>
        )}
      </div>

      {/* Body State Distribution */}
      <div className="space-y-2">
        <span className="text-sm font-medium text-neutral-600">Body States This Week</span>
        <div className="flex flex-wrap gap-2">
          {Object.entries(summary.body_state_distribution).map(([state, count]) => {
            const config = STATE_LABELS[state] || { label: state, color: 'bg-neutral-100 text-neutral-700' };
            return (
              <span
                key={state}
                className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${config.color}`}
              >
                {config.label} × {count}
              </span>
            );
          })}
        </div>
      </div>

      {/* Streak */}
      <div className="flex items-center space-x-2 text-sm text-neutral-600">
        <span>🔥</span>
        <span>{summary.streak_days}-day check-in streak</span>
      </div>

      {/* Reinforcement Message */}
      <div className="bg-white/70 rounded-xl p-4 border border-[#E5D5C3]/50">
        <p className="text-sm text-[#6B5D4F] italic leading-relaxed">
          "{summary.reinforcement_message}"
        </p>
      </div>
    </div>
  );
};

export default WeeklySummaryDisplay;
